import { Localization } from "@viamap/viamap2-common";
import type { LLMAnalysisResultItem, PaataleberettigetResolutionStatus } from "./ANNLLMAnalyzer";
import { normalizeAnnotationTypeValue, normalizeDatoloebenrValue } from "./ANNServitutDocumentAnnotations";
import {
   emptyProvenance,
   type ANNServitutProvenance,
   type ServitutAiFieldKey,
   type ServitutAiFieldStatus,
} from "./ANNServitutProvenance";
import {
   AnnotationContextType,
   AnnotationType,
   type ANNAnnotationRecord,
   type AnnotationTypeCategory,
   type SaveAnnotationInput,
} from "./ANNTypes";

export const SERVITUT_ANALYSIS_FIELDS = ["resume", "entitledParty", "condition", "statement"] as const;
export type ServitutAnalysisFieldKey = typeof SERVITUT_ANALYSIS_FIELDS[number];

export const SERVITUT_FIELD_ANNOTATION_TYPE: Record<ServitutAnalysisFieldKey, AnnotationTypeCategory> = {
   resume: "Resumé",
   entitledParty: "Påtaleberettiget",
   condition: "Rådighed/tilstand",
   statement: "Redegørelse",
};

export type ServitutFieldAnnotationMap = Partial<Record<ServitutAnalysisFieldKey, ANNAnnotationRecord>>;

export function isServitutAnalysisFieldKey(value: string): value is ServitutAnalysisFieldKey {
   return (SERVITUT_ANALYSIS_FIELDS as readonly string[]).includes(value);
}

export function fieldKeyFromAnnotationType(value?: string): ServitutAnalysisFieldKey | null {
   const normalized = normalizeAnnotationTypeValue(value);
   if (normalized === "resumé" || normalized === "resume") {
      return "resume";
   }
   if (normalized === "påtaleberettiget") {
      return "entitledParty";
   }
   if (normalized === "rådighed/tilstand" || normalized === "rådighed" || normalized === "tilstand") {
      return "condition";
   }
   if (normalized === "redegørelse") {
      return "statement";
   }
   return null;
}

export function isServitutFieldNote(item: ANNAnnotationRecord, field?: ServitutAnalysisFieldKey): boolean {
   if (item.type !== AnnotationType.Note) {
      return false;
   }
   const key = fieldKeyFromAnnotationType(item.annotationType);
   if (!key) {
      return false;
   }
   return field ? key === field : true;
}

export function isFieldNoteAccepted(note?: ANNAnnotationRecord | null): boolean {
   return Boolean(note?.verified && (note.note?.text || "").trim());
}

export function listFieldNotesOfType(
   items: ANNAnnotationRecord[],
   field: ServitutAnalysisFieldKey,
   datoloebenr?: string,
): ANNAnnotationRecord[] {
   const target = normalizeDatoloebenrValue(datoloebenr);
   return items.filter((item) => {
      if (!isServitutFieldNote(item, field)) {
         return false;
      }
      if (target && normalizeDatoloebenrValue(item.datoloebenr) !== target) {
         return false;
      }
      return true;
   });
}

export function collectFieldNotes(
   items: ANNAnnotationRecord[],
   datoloebenr?: string,
): ServitutFieldAnnotationMap {
   const map: ServitutFieldAnnotationMap = {};
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      const notes = listFieldNotesOfType(items, field, datoloebenr);
      if (notes.length) {
         map[field] = notes.find((note) => note.verified) || notes[0];
      }
   });
   return map;
}

export function overlayAcceptedFieldStatuses(
   provenance: ANNServitutProvenance | undefined,
   fieldAnnotations?: ServitutFieldAnnotationMap,
): ANNServitutProvenance {
   const fieldStatus = { ...(provenance?.fieldStatus || {}) };
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      if (isFieldNoteAccepted(fieldAnnotations?.[field])) {
         fieldStatus[field] = "person_approved";
      } else if (fieldStatus[field] === "person_approved") {
         delete fieldStatus[field];
      }
   });
   return {
      ...emptyProvenance(),
      ...provenance,
      fieldStatus,
   };
}

export function analysisFieldLabel(field: ServitutAnalysisFieldKey): string {
   switch (field) {
      case "resume":
         return Localization.getText("Resume");
      case "entitledParty":
         return Localization.getText("Entitled party");
      case "condition":
         return Localization.getText("Use / condition");
      case "statement":
         return Localization.getText("Statement");
   }
}

export function provenanceFieldKey(field: ServitutAnalysisFieldKey): Exclude<ServitutAiFieldKey, "pages"> {
   return field;
}

export function getFieldAiStatus(
   provenance: ANNServitutProvenance | undefined,
   field: ServitutAnalysisFieldKey,
): ServitutAiFieldStatus | undefined {
   return provenance?.fieldStatus?.[field];
}

export function getAcceptedFieldText(note?: ANNAnnotationRecord | null): string {
   return (note?.note?.text || "").trim();
}

function formatServitutTypeLabel(value?: string): string {
   if (!value) {
      return "";
   }
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") {
      return Localization.getText("Easement of use");
   }
   if (normalized === "tilstandsservitut") {
      return Localization.getText("Condition easement");
   }
   return value;
}

function formatPaataleStatus(status?: PaataleberettigetResolutionStatus): string {
   switch (status) {
      case "likely":
         return Localization.getText("Likely suggestion");
      case "ambiguous":
         return Localization.getText("Several possible successors");
      case "not_found":
         return Localization.getText("No current entitled party found");
      case "error":
         return Localization.getText("Online search failed");
      default:
         return "";
   }
}

function formatCaseRelevantLabel(value?: boolean): string {
   if (value === true) {
      return Localization.getText("Yes");
   }
   if (value === false) {
      return Localization.getText("No");
   }
   return "";
}

export function buildAiFieldText(
   field: ServitutAnalysisFieldKey,
   analysisItem: LLMAnalysisResultItem | undefined,
   options?: { caseDescription?: string; tingbogSummary?: string },
): string {
   if (field === "resume") {
      return (analysisItem?.summary || options?.tingbogSummary || "").trim();
   }
   if (field === "condition") {
      return formatServitutTypeLabel(analysisItem?.servitut_type);
   }
   if (field === "entitledParty") {
      const resolution = analysisItem?.paataleberettiget_resolution;
      const parts = [
         resolution?.suggested_current_name
            ? `${Localization.getText("AI suggested current")}: ${resolution.suggested_current_name}`
            : resolution?.status
               ? `${Localization.getText("Current")}: ${formatPaataleStatus(resolution.status)}`
               : "",
         analysisItem?.paataleberettiget
            ? `${Localization.getText("Historical")}: ${analysisItem.paataleberettiget}`
            : "",
         resolution?.reasoning || "",
      ].filter(Boolean);
      return parts.join("\n");
   }
   const relevance = formatCaseRelevantLabel(analysisItem?.case_relevant);
   const parts = [
      relevance,
      analysisItem?.case_relevance_reasoning?.trim() || "",
      options?.caseDescription?.trim()
         ? `${Localization.getText("Search terms used")}: ${options.caseDescription.trim()}`
         : "",
   ].filter(Boolean);
   return parts.join("\n");
}

export function getRowFieldText(
   row: {
      analysisItem?: LLMAnalysisResultItem;
      summary?: string;
      fieldAnnotations?: ServitutFieldAnnotationMap;
   },
   field: ServitutAnalysisFieldKey,
   caseDescription?: string,
): string {
   const accepted = getAcceptedFieldText(row.fieldAnnotations?.[field]);
   if (isFieldNoteAccepted(row.fieldAnnotations?.[field]) && accepted) {
      return accepted;
   }
   return buildAiFieldText(field, row.analysisItem, {
      caseDescription,
      tingbogSummary: row.summary,
   });
}

export function hasFieldValueToAccept(
   row: {
      analysisItem?: LLMAnalysisResultItem;
      summary?: string;
      fieldAnnotations?: ServitutFieldAnnotationMap;
   },
   field: ServitutAnalysisFieldKey,
   caseDescription?: string,
): boolean {
   return Boolean(getRowFieldText(row, field, caseDescription).trim());
}

export function isRowFieldAccepted(
   row: { fieldAnnotations?: ServitutFieldAnnotationMap },
   field: ServitutAnalysisFieldKey,
): boolean {
   return isFieldNoteAccepted(row.fieldAnnotations?.[field]);
}

export function buildFieldNoteSaveInput(params: {
   field: ServitutAnalysisFieldKey;
   documentId: string;
   datoLoebenr?: string;
   organizationId: string;
   userId: string;
   userName: string;
   text: string;
   verified: boolean;
   existing?: ANNAnnotationRecord;
   quality?: SaveAnnotationInput["quality"];
   historyComment?: string;
}): SaveAnnotationInput {
   const annotationType = SERVITUT_FIELD_ANNOTATION_TYPE[params.field];
   const title = [
      Localization.getText(annotationType),
      params.datoLoebenr,
   ].filter(Boolean).join(" · ");
   return {
      type: AnnotationType.Note,
      contextType: params.existing?.contextType || AnnotationContextType.Servitut,
      documentId: params.documentId,
      datoloebenr: params.existing?.datoloebenr || params.datoLoebenr,
      organizationId: params.organizationId,
      userId: params.userId,
      userName: params.userName,
      annotationType,
      title,
      note: {
         comment: title,
         text: params.text.trim(),
      },
      quality: params.quality,
      verified: params.verified,
      historyComment: params.historyComment,
      id: params.existing?.id,
      originalCreatedAt: params.existing?.createdAt,
      originalCreatedBy: params.existing?.createdBy,
      originalCreatedByName: params.existing?.createdByName,
   };
}

export async function saveUniqueFieldNote(params: {
   field: ServitutAnalysisFieldKey;
   documentId: string;
   datoLoebenr?: string;
   organizationId: string;
   userId: string;
   userName: string;
   text: string;
   existing?: ANNAnnotationRecord;
   siblings?: ANNAnnotationRecord[];
   verified?: boolean;
   historyComment?: string;
   saveAnnotation: (input: SaveAnnotationInput) => Promise<ANNAnnotationRecord>;
   deleteAnnotation: (id: string, organizationId?: string) => Promise<void>;
}): Promise<ANNAnnotationRecord> {
   const siblings = params.siblings?.length
      ? params.siblings
      : listFieldNotesOfType(params.existing ? [params.existing] : [], params.field, params.datoLoebenr);
   const keep = params.existing
      || siblings.find((note) => note.verified)
      || siblings[0];
   const record = await params.saveAnnotation(buildFieldNoteSaveInput({
      field: params.field,
      documentId: params.documentId,
      datoLoebenr: params.datoLoebenr,
      organizationId: params.organizationId,
      userId: params.userId,
      userName: params.userName,
      text: params.text,
      verified: params.verified !== false,
      existing: keep,
      historyComment: params.historyComment,
   }));
   for (const extra of siblings) {
      if (!extra.id || extra.id === record.id) {
         continue;
      }
      try {
         await params.deleteAnnotation(extra.id, params.organizationId);
      } catch (error) {
         console.warn("[Servitutredegørelse] failed to delete duplicate field annotation", extra.id, error);
      }
   }
   return record;
}
